import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Coins } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import DepositParametersSection from './DepositParametersSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import ContributionsSection from './ContributionsSection';
import WithdrawalsSection from './WithdrawalsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета вклада
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  schedule,
  setSchedule,
  depositTypes,
  frequencyOptions,
  capitalizationOptions,
  currencyOptions,
  saveDeposit 
}) => {
  
  // Состояние для имени сохраняемого вклада
  const [saveDepositName, setSaveDepositName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем процентную ставку автоматически при изменении типа вклада
      if (name === 'depositType') {
        const selectedDepositType = depositTypes.find(type => type.id === fieldValue);
        if (selectedDepositType) {
          const currentRate = parseFloatOrDefault(prev.interestRate, 0);
          const isRateDefault = depositTypes.some(dt => currentRate === dt.defaultRate);
          if (isRateDefault || !prev.interestRate || currentRate <= 0) {
            newState.interestRate = selectedDepositType.defaultRate.toString();
          }
        }
      }

      // Сбрасываем связанные поля если функция отключена
      if (name === 'additionalContributions' && !checked) {
        newState.contributionFrequency = 'monthly';
        newState.contributionAmount = '10000';
        newState.contributionStartMonth = '2';
      }
      
      if (name === 'withdrawals' && !checked) {
        newState.withdrawalFrequency = 'quarterly';
        newState.withdrawalAmount = '5000';
        newState.withdrawalStartMonth = '3';
      }
      
      if (name === 'earlyTermination' && !checked) {
        newState.earlyTerminationMonth = '6';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [depositTypes, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      depositAmount, depositTerm, interestRate, startDate, capitalization,
      additionalContributions, contributionAmount, contributionStartMonth, contributionFrequency,
      withdrawals, withdrawalAmount, withdrawalStartMonth, withdrawalFrequency,
      earlyTermination, earlyTerminationMonth
    } = formData;

    // Парсинг числовых значений
    const numDepositAmount = parseFloatOrDefault(depositAmount, -1);
    const numDepositTerm = parseIntOrDefault(depositTerm, -1);
    const numInterestRate = parseFloatOrDefault(interestRate, -1);

    // Валидация базовых полей
    if (numDepositAmount <= 0) newErrors.depositAmount = 'Сумма должна быть положительной.';
    if (!newErrors.depositAmount && numDepositAmount > 100000000) newErrors.depositAmount = 'Максимальная сумма 100 млн.';
    
    if (numDepositTerm <= 0) newErrors.depositTerm = 'Срок должен быть положительным.';
    if (!newErrors.depositTerm && numDepositTerm > 120) newErrors.depositTerm = 'Максимальный срок 120 месяцев (10 лет).';
    
    if (numInterestRate <= 0) newErrors.interestRate = 'Ставка должна быть положительной.';
    if (!newErrors.interestRate && numInterestRate > 100) newErrors.interestRate = 'Ставка не может быть более 100%.';

    // Проверка даты начала вклада
    if (!startDate) newErrors.startDate = 'Укажите дату открытия вклада.';
    try {
      const dateObject = new Date(startDate);
      if (isNaN(dateObject.getTime())) {
        newErrors.startDate = 'Некорректная дата.';
      }
    } catch (e) {
      newErrors.startDate = 'Некорректная дата.';
    }

    // Валидация капитализации
    if (!capitalization || !['none', 'daily', 'monthly', 'quarterly', 'annually'].includes(capitalization)) {
      newErrors.capitalization = "Выберите корректный тип капитализации.";
    }

    // Валидация дополнительных взносов
    if (additionalContributions) {
      const numContributionAmount = parseFloatOrDefault(contributionAmount, -1);
      const numContributionStartMonth = parseIntOrDefault(contributionStartMonth, -1);
      
      if (numContributionAmount <= 0) newErrors.contributionAmount = 'Сумма взноса должна быть положительной.';
      if (!isInteger(contributionStartMonth) || numContributionStartMonth <= 0) {
        newErrors.contributionStartMonth = 'Введите целый положительный номер месяца.';
      }
      if (!newErrors.depositTerm && !newErrors.contributionStartMonth && numContributionStartMonth > numDepositTerm) {
        newErrors.contributionStartMonth = `Начало (${numContributionStartMonth}) не может быть позже срока (${numDepositTerm} мес.).`;
      }
      if (!contributionFrequency) newErrors.contributionFrequency = "Выберите периодичность взносов.";
    }

    // Валидация снятий
    if (withdrawals) {
      const numWithdrawalAmount = parseFloatOrDefault(withdrawalAmount, -1);
      const numWithdrawalStartMonth = parseIntOrDefault(withdrawalStartMonth, -1);
      
      if (numWithdrawalAmount <= 0) newErrors.withdrawalAmount = 'Сумма снятия должна быть положительной.';
      if (!isInteger(withdrawalStartMonth) || numWithdrawalStartMonth <= 0) {
        newErrors.withdrawalStartMonth = 'Введите целый положительный номер месяца.';
      }
      if (!newErrors.depositTerm && !newErrors.withdrawalStartMonth && numWithdrawalStartMonth > numDepositTerm) {
        newErrors.withdrawalStartMonth = `Начало (${numWithdrawalStartMonth}) не может быть позже срока (${numDepositTerm} мес.).`;
      }
      if (!withdrawalFrequency) newErrors.withdrawalFrequency = "Выберите периодичность снятий.";
    }

    // Валидация досрочного закрытия
    if (earlyTermination) {
      const numEarlyTerminationMonth = parseIntOrDefault(earlyTerminationMonth, -1);
      
      if (!isInteger(earlyTerminationMonth) || numEarlyTerminationMonth <= 0) {
        newErrors.earlyTerminationMonth = 'Введите целый положительный номер месяца.';
      }
      if (!newErrors.depositTerm && !newErrors.earlyTerminationMonth && numEarlyTerminationMonth >= numDepositTerm) {
        newErrors.earlyTerminationMonth = `Месяц закрытия (${numEarlyTerminationMonth}) должен быть раньше срока вклада (${numDepositTerm} мес.).`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета графика накоплений
  const calculateDepositSchedule = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setSchedule([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const initialDepositAmount = parseFloatOrDefault(formData.depositAmount);
        const depositTermMonths = parseIntOrDefault(formData.depositTerm);
        const annualInterestRate = parseFloatOrDefault(formData.interestRate);
        const capitalization = formData.capitalization;
        const startDateObj = new Date(formData.startDate);
        const taxRate = formData.tax ? 0.13 : 0; // 13% налог на доходы свыше необлагаемой суммы
        const taxExemptLimit = 1000000 * 0.12; // Необлагаемая сумма - доход с 1 млн при ставке ЦБ 12%

        const additionalContributions = formData.additionalContributions;
        const contributionFrequency = formData.contributionFrequency;
        const contributionAmount = additionalContributions ? parseFloatOrDefault(formData.contributionAmount, 0) : 0;
        const contributionStartMonth = additionalContributions ? parseIntOrDefault(formData.contributionStartMonth, 1) : 1;

        const withdrawals = formData.withdrawals;
        const withdrawalFrequency = formData.withdrawalFrequency;
        const withdrawalAmount = withdrawals ? parseFloatOrDefault(formData.withdrawalAmount, 0) : 0;
        const withdrawalStartMonth = withdrawals ? parseIntOrDefault(formData.withdrawalStartMonth, 1) : 1;

        const earlyTermination = formData.earlyTermination;
        const earlyTerminationMonth = earlyTermination ? parseIntOrDefault(formData.earlyTerminationMonth, depositTermMonths) : null;

        // --- Определение периодов капитализации в месяцах ---
        const capitalizationPeriods = {
          none: 0, // без капитализации
          daily: 1 / 30, // примерное количество месяцев в дне
          monthly: 1,
          quarterly: 3,
          annually: 12
        };
        const capPeriod = capitalizationPeriods[capitalization] || 0;

        // --- Инициализируем переменные для расчета ---
        let balance = initialDepositAmount;
        let totalInterest = 0;
        let totalTax = 0;
        let totalContributions = 0;
        let totalWithdrawals = 0;
        let actualTermMonths = earlyTermination ? Math.min(earlyTerminationMonth, depositTermMonths) : depositTermMonths;

        const calculatedSchedule = [];
        
        // --- Расчет ежемесячной процентной ставки ---
        const monthlyRate = annualInterestRate / 100 / 12;

        // --- Цикл расчета графика накоплений ---
        for (let month = 1; month <= actualTermMonths; month++) {
          const paymentDate = new Date(startDateObj);
          paymentDate.setMonth(startDateObj.getMonth() + month - 1);
          
          // Начальный баланс для записи
          const startingBalance = balance;
          
          // Рассчитываем пополнения
          let contribution = 0;
          if (additionalContributions && month >= contributionStartMonth) {
            if (contributionFrequency === 'once' && month === contributionStartMonth) {
              contribution = contributionAmount;
            } else if (contributionFrequency === 'monthly') {
              contribution = contributionAmount;
            } else if (contributionFrequency === 'quarterly' && (month - contributionStartMonth) % 3 === 0) {
              contribution = contributionAmount;
            } else if (contributionFrequency === 'annually' && (month - contributionStartMonth) % 12 === 0) {
              contribution = contributionAmount;
            }
          }
          
          // Добавляем пополнение к балансу
          if (contribution > 0) {
            balance += contribution;
            totalContributions += contribution;
          }
          
          // Рассчитываем снятия
          let withdrawal = 0;
          if (withdrawals && month >= withdrawalStartMonth) {
            if (withdrawalFrequency === 'once' && month === withdrawalStartMonth) {
              withdrawal = Math.min(withdrawalAmount, balance * 0.9); // Не даем снять более 90% баланса
            } else if (withdrawalFrequency === 'monthly') {
              withdrawal = Math.min(withdrawalAmount, balance * 0.9);
            } else if (withdrawalFrequency === 'quarterly' && (month - withdrawalStartMonth) % 3 === 0) {
              withdrawal = Math.min(withdrawalAmount, balance * 0.9);
            } else if (withdrawalFrequency === 'annually' && (month - withdrawalStartMonth) % 12 === 0) {
              withdrawal = Math.min(withdrawalAmount, balance * 0.9);
            }
          }
          
          // Вычитаем снятие из баланса
          if (withdrawal > 0) {
            balance -= withdrawal;
            totalWithdrawals += withdrawal;
          }
          
          // Рассчитываем проценты за месяц
          let monthlyInterest = balance * monthlyRate;
          
          // Для ежедневной капитализации (примерное приближение)
          if (capitalization === 'daily') {
            const dailyRate = monthlyRate / 30;
            let dailyBalance = balance;
            monthlyInterest = 0;
            
            for (let day = 0; day < 30; day++) {
              const dailyInterest = dailyBalance * dailyRate;
              dailyBalance += dailyInterest;
              monthlyInterest += dailyInterest;
            }
          }
          
          totalInterest += monthlyInterest;
          
          // Капитализация процентов
          let capitalizedInterest = 0;
          if (capPeriod > 0) {
            // Для ежедневной капитализации уже учтено выше
            if (capitalization !== 'daily') {
              // Проверяем, является ли текущий месяц месяцем капитализации
              const isCapitalizationMonth = 
                (capitalization === 'monthly') || 
                (capitalization === 'quarterly' && month % 3 === 0) || 
                (capitalization === 'annually' && month % 12 === 0);
                
              if (isCapitalizationMonth) {
                capitalizedInterest = monthlyInterest;
                balance += capitalizedInterest;
              }
            } else {
              capitalizedInterest = monthlyInterest;
              balance += capitalizedInterest;
            }
          }
          
          // Добавляем запись в график
          calculatedSchedule.push({
            month,
            date: paymentDate,
            startingBalance,
            contribution,
            withdrawal,
            interest: monthlyInterest,
            capitalizedInterest,
            endingBalance: balance,
            totalInterestToDate: totalInterest
          });
        }
        
        // --- Расчет налога ---
        const effectiveInterest = totalInterest - totalWithdrawals;
        if (formData.tax && effectiveInterest > taxExemptLimit) {
          totalTax = (effectiveInterest - taxExemptLimit) * taxRate;
        }
        
        // Итоговый баланс с учетом налога
        const finalBalance = balance - totalTax;
        const finalAmount = earlyTermination ? finalBalance : balance;
        
        // --- Готовим итоговые результаты ---
        const finalResults = {
          initialAmount: initialDepositAmount,
          finalAmount: finalAmount,
          interestAmount: totalInterest,
          taxAmount: totalTax,
          netGain: totalInterest - totalTax,
          effectiveRate: (totalInterest / initialDepositAmount) * (12 / actualTermMonths) * 100,
          totalContributions,
          totalWithdrawals,
          actualTermMonths,
          capitalization,
          currency: formData.currency,
          
          depositStartDate: startDateObj,
          depositEndDate: calculatedSchedule.length > 0 ? calculatedSchedule[calculatedSchedule.length - 1].date : null,
          
          scheduleShort: calculatedSchedule.slice(0, 12) // Первые 12 месяцев для предпросмотра
        };
        
        setResults(finalResults);
        setSchedule(calculatedSchedule);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setSchedule([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setSchedule, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      depositAmount: '300000',
      depositTerm: '12',
      interestRate: '8.5',
      capitalization: 'monthly',
      startDate: new Date().toISOString().split('T')[0],
      depositType: 'standard',
      currency: 'RUB',
      tax: true,
      additionalContributions: false,
      contributionFrequency: 'monthly',
      contributionAmount: '10000',
      contributionStartMonth: '2',
      withdrawals: false,
      withdrawalFrequency: 'quarterly',
      withdrawalAmount: '5000',
      withdrawalStartMonth: '3',
      earlyTermination: false,
      earlyTerminationMonth: '6',
    });
    setResults(null);
    setSchedule([]);
    setErrors({});
    setSaveDepositName('');
    setShowSaveDialog(false);
  };

  // Экспорт графика в CSV
  const exportToCSV = () => {
    if (!schedule || schedule.length === 0 || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки на основе расчетов
    const headers = ['Месяц', 'Дата', 'Начальный баланс', 'Пополнение', 'Снятие', 'Проценты', 'Капитализация', 'Итоговый баланс'];

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');
    
    // Выбираем символ валюты
    const currencySymbol = currencyOptions.find(c => c.id === formData.currency)?.symbol || '';

    const rows = schedule.map(item => {
      return [
        item.month,
        item.date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }),
        formatCsvNumber(item.startingBalance),
        formatCsvNumber(item.contribution),
        formatCsvNumber(item.withdrawal),
        formatCsvNumber(item.interest),
        formatCsvNumber(item.capitalizedInterest),
        formatCsvNumber(item.endingBalance)
      ].join(';');
    });

    // Добавим итоговую строку
    const totalRow = [
      '', 'ИТОГО:', 
      formatCsvNumber(results.initialAmount),
      formatCsvNumber(results.totalContributions),
      formatCsvNumber(results.totalWithdrawals),
      formatCsvNumber(results.interestAmount),
      formatCsvNumber(results.interestAmount),
      formatCsvNumber(results.finalAmount)
    ].join(';');
    
    // Добавим основную информацию в начало файла
    const capTypeName = capitalizationOptions.find(c => c.id === results.capitalization)?.name || '';
    
    const infoRows = [
      ['Сумма вклада:', `${formatCsvNumber(results.initialAmount)} ${currencySymbol}`],
      ['Срок вклада:', `${results.actualTermMonths} мес.`],
      ['Процентная ставка:', `${formData.interestRate}%`],
      ['Капитализация:', capTypeName],
      ['Дата открытия вклада:', new Date(formData.startDate).toLocaleDateString('ru-RU')],
      ['Доход:', `${formatCsvNumber(results.netGain)} ${currencySymbol}`],
      ['НДФЛ:', `${formatCsvNumber(results.taxAmount)} ${currencySymbol}`],
      ['Итоговая сумма:', `${formatCsvNumber(results.finalAmount)} ${currencySymbol}`],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'график_накоплений_по_вкладу.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения вклада
  const handleSaveDeposit = () => {
    if (showSaveDialog) {
      const savedResult = saveDeposit(saveDepositName || null);
      if (savedResult) {
        alert(`Вклад "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveDepositName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateDepositSchedule();
  };
  
  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Coins className="w-6 h-6 text-green-500" />
          Параметры вклада
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры вклада */}
        <DepositParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          depositTypes={depositTypes}
          capitalizationOptions={capitalizationOptions}
          currencyOptions={currencyOptions}
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Пополнения */}
        <ContributionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          frequencyOptions={frequencyOptions}
        />

        {/* Снятия */}
        <WithdrawalsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          frequencyOptions={frequencyOptions}
        />

        {/* Диалог сохранения вклада */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveDepositName}
                onChange={(e) => setSaveDepositName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveDeposit}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать доходность'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {schedule.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать график (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveDeposit}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;